import { log } from 'evlog/client';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData } from 'react-router';

import { client } from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import { ServicePayment } from '../../components/booking/service-payment.tsx';
import {
  isStandardReady,
  standardReservationStore,
} from '../../hooks/reservation/standard.ts';
import { usePaymentInfo } from '../../hooks/use-payment-info.ts';

export default function BookingPayments() {
  const loaderData = useLoaderData() as Awaited<
    ReturnType<typeof bookingPaymentsLoader>
  >;
  const { t } = useTranslation();
  const payment = usePaymentInfo({
    kind: 'standard',
    appliedPromo: loaderData.reservation.appliedPromo,
  });
  const amount = payment.amountDue;

  const handleInitiating = async () => {
    log.info({ action: 'payment_initiate', service: 'standard' });
    const result = await client.request(
      'POST /reservations/check-availability',
      {
        specialityId: loaderData.reservation.specialityId,
        scheduledDate: loaderData.reservation.scheduledDate,
        scheduledMinutes: loaderData.reservation.scheduledMinutes,
      },
    );
    return result.canBook;
  };

  return (
    <ServicePayment
      serviceLabel={t('common.services.standard')}
      amountLabel={amount.label}
      amountValue={amount.value}
      description={t('booking.paymentDescription')}
      metadata={{ transferables: JSON.stringify(buildMetadata(loaderData)) }}
      onInitiating={handleInitiating}
    />
  );
}

export async function bookingPaymentsLoader() {
  const identity = await requiresAuth();
  const reservation = standardReservationStore.read();
  if (identity.userType === 'B2B') {
    throw redirect('/');
  }
  if (!reservation || !isStandardReady(reservation)) {
    throw redirect('/book-appointment');
  }
  return { identity, reservation };
}

function buildMetadata(
  loaderData: Awaited<ReturnType<typeof bookingPaymentsLoader>>,
) {
  const { appliedPromo, ...rest } = loaderData.reservation;
  return {
    ...rest,
    service: 'standard',
    promoCode: appliedPromo?.code,
    uid: loaderData.identity.id,
  };
}
